import React, { PureComponent } from 'react';
import { debounce, isFunction, isString } from 'lodash';
import { InputAdornment, IconButton } from '@mui/material';
import { chargerDetailsInputDebounce } from '../../constants/commonConstants';
import MapDialog from './MapDialog';
import TextField from '../TextField/TextField';
import Autocomplete from '../Autocomplete/Autocomplete';
import MenuItem from '../MenuItem/MenuItem';

const readOnlyUnderlineStyles = { borderBottom: '1px solid rgb(224, 224, 224)' };
const googleMapAutocompleteContainerStyle = { top: '-14px' };

function AddressIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g opacity="0.5">
        <path
          d="M5.8335 15C4.30939 15.3431 3.3335 15.8702 3.3335 16.4614C3.3335 17.4952 6.31826 18.3333 10.0002 18.3333C13.6821 18.3333 16.6668 17.4952 16.6668 16.4614C16.6668 15.8702 15.6909 15.3431 14.1668 15"
          stroke="#374649"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
        <path
          d="M11.0482 14.578C10.7671 14.8487 10.3914 15 10.0005 15C9.60948 15 9.23381 14.8487 8.95274 14.578C6.37891 12.084 2.92965 9.29795 4.61175 5.25312C5.52125 3.06611 7.70445 1.66667 10.0005 1.66667C12.2965 1.66667 14.4797 3.06611 15.3892 5.25312C17.0691 9.29285 13.6283 12.0926 11.0482 14.578Z"
          stroke="#374649"
          strokeWidth="1.2"
        />
        <path d="M10 5V10" stroke="#374649" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M7.5 7.5H12.5" stroke="#374649" strokeWidth="1.2" strokeLinecap="round" />
      </g>
    </svg>
  );
}

class RenderGooglePlaceAutocomplete extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      mapOpen: false,
      overrideOpen: false,
      addressIsValid: !!props.c_address,
      placeId: props.c_address,
    };
  }

  onUpdateTextInput = (searchText, type) => {
    const {
      input: { onChange },
    } = this.props;
    const addressIsValid = type !== 'input';

    onChange(searchText);
    this.forwardChange(searchText, addressIsValid);
    this.setState({ addressIsValid });
  };

  onUpdateInput = (value, isValidAdress) => {
    this.props.onUpdateInput(value, isValidAdress);
    this.setState({ placeId: null });
  };

  forwardChange = debounce(this.onUpdateInput, chargerDetailsInputDebounce);

  isAdressNew = (marker) => this.props.c_address && marker.formatted_address !== this.props.c_address;

  onHandleSubmit = (marker, geometry) => (this.isAdressNew(marker) ? this.handleOverrideOpen() : this.handleSubmitModal(marker, geometry));

  handleSubmitModal = (marker, geometry) => {
    const {
      input: { onChange },
      onUpdateInputFromPicker,
    } = this.props;

    onChange(marker.formatted_address || '');
    onUpdateInputFromPicker(marker, geometry);
    this.handleCloseModal();
  };

  onNewRequest = (chosenRequest) => {
    const {
      input: { onChange },
      onItemSelected,
    } = this.props;
    const { value, text } = chosenRequest;

    this.setState({ placeId: value });
    onChange(text);
    onItemSelected(chosenRequest);
  };

  handleOpenModal = () => {
    if (this.props.countrySelected) {
      this.setState({ mapOpen: true });
    } else {
      this.props.input.onFocus();
    }
  };

  handleCloseModal = () => {
    this.setState({ mapOpen: false });
  };

  handleOverrideOpen = () => {
    this.setState({ overrideOpen: true });
  };

  handleOverrideClose = () => {
    this.setState({ overrideOpen: false });
  };

  handleToggleOverride = (override, marker, geometry) => {
    const {
      input: { onChange },
      onUpdateInputFromPicker,
    } = this.props;

    if (override) {
      onUpdateInputFromPicker(marker, geometry);
      onChange(marker.formatted_address || '');
    }

    this.handleOverrideClose();
    this.handleCloseModal();
  };

  makeInputStyles = () => {
    const { readOnly, countrySelected } = this.props;
    const inputStyles = {};

    if (countrySelected) inputStyles.paddingRight = '38px';
    if (readOnly) inputStyles.color = 'black';

    return inputStyles;
  };

  handleBlur = (event, newValue) => {
    const {
      input: { onBlur, value, onChange },
      dataSource,
      onItemSelected,
    } = this.props;

    // Checking if user missed address selection
    setTimeout(() => {
      if (value && !this.state.addressIsValid && !this.state.placeId) {
        // Open google map if we have the multiple choice or have nothing depends of input string
        if (dataSource.length === 1) {
          // Set as selected single match
          const place = dataSource[0];

          this.setState({ placeId: place.value });
          onChange(place.text);
          onItemSelected(place);
        }
      }
    }, 500);

    // Event delegation
    if (isFunction(onBlur)) onBlur(event, newValue);
  };

  render() {
    const {
      meta: { touched, error },
      input: { name, onFocus, value },
      label,
      floatingLabelText,
      required,
      disabled,
      readOnly,
      className,
      dataSource = [],
    } = this.props;

    const formattedErrorText = touched && error ? error : '';
    const formattedLabel = label || floatingLabelText || '';
    const labelText = required ? (
      <span>
        {formattedLabel} <span className="required-asterisk">*</span>
      </span>
    ) : (
      formattedLabel
    );
    const iconStyle = readOnly ? { display: 'none' } : { opacity: 0.65 };
    const inputStyles = this.makeInputStyles();

    return (
      <div style={googleMapAutocompleteContainerStyle} className={`address-picker-container ${className}`} data-field-name={`${name}`}>
        <Autocomplete
          forcePopupIcon={false}
          disableClearable
          className={readOnly ? 'cursor-auto' : ''}
          name={name}
          options={dataSource}
          onChange={(event, value, reason) => {
            if (reason === 'selectOption') {
              this.onNewRequest(value);
            }
          }}
          getOptionLabel={(option) => (isString(option) ? option : option.text)}
          isOptionEqualToValue={(option, value) => (isString(value) ? value === option.text : value.text === option.text)}
          filterOptions={(x) => x}
          onInputChange={(event, value, type) => {
            if (type === 'reset') {
              return false;
            }
            this.onUpdateTextInput(value, type);
          }}
          inputValue={value || ''}
          onFocus={onFocus}
          onBlur={this.handleBlur}
          fullWidth
          autoSelect
          renderInput={(params) => (
            <TextField
              {...params}
              style={inputStyles}
              fullWidth
              label={labelText}
              placeholder="Please select country first"
              InputProps={{
                ...params.InputProps,
                endAdornment: !readOnly ? (
                  <InputAdornment position="end">
                    <IconButton size="large" style={iconStyle} onClick={this.handleOpenModal}>
                      <AddressIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              InputLabelProps={{ shrink: true }}
              error={touched && error ? !!error : false}
              helperText={isString(formattedErrorText) ? formattedErrorText : ''}
            />
          )}
          disabled={disabled || readOnly}
          style={readOnly && disabled ? readOnlyUnderlineStyles : {}}
          renderOption={(props, option, { selected }) => (
            <MenuItem title={option.text} value={option.text} key={option.text} {...props}>
              {option.text}
            </MenuItem>
          )}
        />
        <MapDialog
          handleCloseModal={this.handleCloseModal}
          onHandleSubmit={this.onHandleSubmit}
          handleToggleOverride={this.handleToggleOverride}
          handleOverrideClose={this.handleOverrideClose}
          overrideOpen={this.state.overrideOpen}
          mapOpen={this.state.mapOpen}
          countrySelected={this.props.countrySelected}
          addressIsValid={this.state.addressIsValid}
          dataSource={dataSource}
          c_latitude={this.props.c_latitude}
          c_longitude={this.props.c_longitude}
          c_address={this.props.c_address}
        />
      </div>
    );
  }
}

export default RenderGooglePlaceAutocomplete;
